import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Store, Plus, ArrowLeft, Key, LogOut, Trash2, Edit2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/shared/Modal";

export default function ManageMerchants({ onBack } = {}) {
  const [merchants, setMerchants] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const { authAxios } = useAuth();

  useEffect(() => { fetchMerchants(); }, []);

  const fetchMerchants = async () => {
    const res = await authAxios.get("/merchants");
    setMerchants(res.data);
  };

  const handleAdd = async () => {
    if (!name || !email) return toast.error("Fill all fields");
    try {
      await authAxios.post("/merchants", { name, email });
      toast.success("Merchant added. Login credentials sent via email.");
      setShowAdd(false); setName(""); setEmail("");
      fetchMerchants();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdate = async () => {
    if (!name || !email) return toast.error("Fill all fields");
    try {
      await authAxios.put(`/merchants/${editItem.id}`, { name, email });
      toast.success("Updated");
      setEditItem(null); setName(""); setEmail("");
      fetchMerchants();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleToggle = async (id, isActive) => {
    await authAxios.put(`/merchants/${id}`, { is_active: !isActive });
    fetchMerchants();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this merchant?")) return;
    await authAxios.delete(`/merchants/${id}`);
    toast.success("Deleted");
    fetchMerchants();
  };

  const handleResetPassword = async (id) => {
    await authAxios.post(`/merchants/${id}/reset-password`);
    toast.success("Password reset email sent");
    fetchMerchants();
  };

  const handleLogoutUser = async (id) => {
    await authAxios.post(`/merchants/${id}/logout`);
    toast.success("Merchant logged out");
    fetchMerchants();
  };

  return (
    <div data-testid="manage-merchants">
      <div className="flex items-center justify-between mb-6">
        {onBack && <button onClick={onBack} className="flex items-center gap-2 text-slate-600"><ArrowLeft className="w-5 h-5" /> Merchants</button>}
        <button onClick={() => setShowAdd(true)} className="w-10 h-10 rounded-full bg-[#2d8a4e] text-white flex items-center justify-center" data-testid="add-merchant-btn"><Plus className="w-5 h-5" /></button>
      </div>

      {merchants.length === 0 ? (
        <div className="text-center py-20 text-slate-400"><Store className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No merchants yet</p></div>
      ) : (
        <div className="space-y-3">
          {merchants.map(m => (
            <div key={m.id} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Store className="w-6 h-6 text-green-500" /></div>
                  <div>
                    <p className="font-bold text-slate-800">{m.name}</p>
                    <p className="text-slate-500 text-sm">{m.email}</p>
                    {m.is_logged_in && <span className="text-xs text-green-500">Online</span>}
                  </div>
                </div>
                <button onClick={() => handleToggle(m.id, m.is_active)} className={`px-3 py-1 rounded-full text-sm font-medium ${m.is_active ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"}`}>
                  {m.is_active ? "Active" : "Inactive"}
                </button>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => { setEditItem(m); setName(m.name); setEmail(m.email); }} className="flex items-center gap-1 text-blue-500 text-sm"><Edit2 className="w-4 h-4" /> Edit</button>
                <button onClick={() => handleResetPassword(m.id)} className="flex items-center gap-1 text-amber-500 text-sm"><Key className="w-4 h-4" /> Reset Password</button>
                {m.is_logged_in && <button onClick={() => handleLogoutUser(m.id)} className="flex items-center gap-1 text-orange-500 text-sm"><LogOut className="w-4 h-4" /> Logout</button>}
                <button onClick={() => handleDelete(m.id)} className="flex items-center gap-1 text-red-400 text-sm ml-auto"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <Modal title="Add Merchant" onClose={() => setShowAdd(false)}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full p-3 rounded-xl border mb-3" data-testid="merchant-name" />
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" className="w-full p-3 rounded-xl border mb-4" data-testid="merchant-email" />
        <button onClick={handleAdd} className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold" data-testid="save-merchant">Add Merchant</button>
      </Modal>}

      {editItem && <Modal title="Edit Merchant" onClose={() => { setEditItem(null); setName(""); setEmail(""); }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full p-3 rounded-xl border mb-3" />
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" className="w-full p-3 rounded-xl border mb-4" />
        <button onClick={handleUpdate} className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold">Update</button>
      </Modal>}
    </div>
  );
}
